import React, { useEffect, useState} from 'react';
 
interface ActiveParticipantsProps {
    active: number;
    yesterday: number;
    last_week: number;
    inactive: number;
    width: number;
    height: number;
  }

const ActiveParticipants: React.FC<ActiveParticipantsProps> = ({
  active, 
  yesterday,
  last_week,
  inactive,
  width,
  height
}) => {
  const [radius, setRadius] = useState(100);
  const [yesterdayStrokeOffset, setYesterdayStrokeOffset] = useState(0);
  const [lastWeekStrokeOffset, setLastWeekStrokeOffset] = useState(0);
  const [inactiveStrokeOffset, setInactiveStrokeOffset] = useState(0);
  const [fontSize, setFontSize] = useState(12); // Default font size
  const [strokeWidth, setStrokeWidth] = useState(10); // Default font size
  const [totalUsers, setTotalUsers] = useState(1);


  const updateRadius = () => {
    if (width > 0 && height > 0) {
      // Dynamically set radius based on container's width
      const newRadius = width / 3; // Adjust this ratio as needed
      setRadius(newRadius);
      const newFontSize = Math.max(width / 16, 6); // Set a minimum font size of 12
      setFontSize(newFontSize);
      const newStrokeWidth = Math.max(newRadius / 6, 10); // Set a minimum font size of 12
      setStrokeWidth(newStrokeWidth);
    }
  };

  useEffect(() => {
    // Update the circle radius dynamically based on container width
    // + initial update on component mount
    updateRadius();   
  }, [active, yesterday, last_week, inactive, width, height]);

  const normalizedRadius = radius - strokeWidth / 2; // Adjust radius to fit within stroke
  const circumference = 2 * Math.PI * normalizedRadius; // Circumference of the circle

  // Update strokeDashoffset when div or radius changes
  useEffect(() => {
    const total = active + yesterday + last_week + inactive || 1;
    const inactiveOffset = Math.min(inactive / total, 1) * circumference;
    const last_weekOffset = Math.min((last_week + inactive)/ total, 1) * circumference;
    const yesterdayOffset = Math.min((last_week + inactive + yesterday) / total, 1) * circumference;

    setTotalUsers(total);
    setYesterdayStrokeOffset( yesterdayOffset);
    setLastWeekStrokeOffset( last_weekOffset);
    setInactiveStrokeOffset( inactiveOffset);
  }, [active, yesterday, last_week, inactive, circumference]);

  return (
    <div style={{ width: '100%', margin: '0 auto'}}>
      <header style={{ fontWeight: '700', paddingBottom: '5%', fontSize: fontSize*1.25}}>
        ACTIVE PARTICIPANTS
      </header>
      <svg width={radius * 2} height={radius * 2} style={{marginLeft: `${radius/2}px`}}>
          {/* inactive circle */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            fill="transparent"
            stroke="#60c380" //green
            strokeWidth={strokeWidth}
          /> 
          {/* last week circle */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            fill="transparent"
            stroke="#f5a3ab" //pink
            strokeWidth={strokeWidth}
            strokeDasharray={circumference} // Total length of the circle
            strokeDashoffset={inactiveStrokeOffset} // How much of the circle to "hide"
            transform={`rotate(-90 ${radius} ${radius})`}
          />
          {/* yesterday circle */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            fill="transparent"
            stroke="#a790e1" //purple
            strokeWidth={strokeWidth}
            strokeDasharray={circumference} // Total length of the circle
            strokeDashoffset={lastWeekStrokeOffset} // How much of the circle to "hide"
            transform={`rotate(-90 ${radius} ${radius})`}
          />
          {/* active circle */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            fill="transparent"
            stroke= "#5095fd" //blue 
            strokeWidth={strokeWidth}
            strokeDasharray={circumference} // Total length of the circle
            strokeDashoffset={yesterdayStrokeOffset} // How much of the circle to "hide"
            transform={`rotate(-90 ${radius} ${radius})`}
          />
          
        <text
          x="50%"
          y="45%"
          textAnchor="middle"
          alignmentBaseline="middle"
          fontSize={fontSize*1.5} 
          fill="black" 
          fontWeight="800" 
        >
          {active + yesterday + last_week + inactive}
        </text>
        <text
          x="50%"   // Horizontal position at 50% of the SVG width (centered)
          y="55%"   // Vertical position below "Total sales"
          alignmentBaseline="middle" 
          textAnchor="middle" 
          fontSize={fontSize*1.1} 
          fill="black" 
          fontWeight="700" 
          letterSpacing="0"
        >
          users
        </text>
      </svg>
      <div style={{ textAlign: 'center', marginTop: '5px', width: '100%'}}>
        {/* Legend Section */}
        <div style={{ marginTop: '0px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {/* first row Legend */}
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '10px'}}>
            <div style={{ display: 'flex', alignItems: 'center',  flex: 1 }}>
              <div
                style={{
                  width: fontSize,
                  height: fontSize,
                  backgroundColor: '#5095fd',
                  borderRadius: '50%',
                  marginRight: '4px',
                  flexShrink: 0, // Prevent the circle from shrinking
                }}
              ></div>
              <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start', marginLeft:'2px'}}>
                <div style={{fontSize: `${fontSize*.8}px`, fontWeight:'400'}}>Active</div>
                <div style={{fontSize: `${fontSize*.95}px`, fontWeight:'500'}}> {(active / (totalUsers) * 100).toFixed(2)}%</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1  }}>
              <div
                style={{
                  width: fontSize,
                  height: fontSize,
                  backgroundColor: '#a790e1',
                  borderRadius: '50%',
                  marginRight: '4px',
                  marginLeft: '4px',
                  flexShrink: 0, // Prevent the circle from shrinking
                }}
              ></div>
              <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start', marginLeft:'2px'}}>
                <div style={{fontSize: `${fontSize*.8}px`, textAlign: 'start',fontWeight:'400'}}>Active yesterday</div>
                <div style={{fontSize: `${fontSize*.95}px`, fontWeight:'500'}}> {(yesterday / (totalUsers) * 100).toFixed(2)}%</div>
              </div>
            </div>
          </div>

          {/* row 2 Legend */}
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '10px', marginTop: '10%'}}>
            <div style={{ display: 'flex', alignItems: 'center',  flex: 1 }}>
              <div
                style={{
                  width: fontSize,
                  height: fontSize,
                  backgroundColor: '#f5a3ab',
                  borderRadius: '50%',
                  marginRight: '4px',
                  flexShrink: 0, // Prevent the circle from shrinking
                }}
              ></div>
              <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start', marginLeft:'2px'}}>
                <div style={{fontSize: `${fontSize*.8}px`, textAlign: 'start', fontWeight:'400'}}>Active last week</div>
                <div style={{fontSize: `${fontSize*.95}px`, fontWeight:'500'}}> {(last_week / totalUsers * 100).toFixed(2)}%</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center',  flex: 1 }}>
              <div
                style={{
                  width: fontSize,
                  height: fontSize,
                  backgroundColor: '#60c380',
                  borderRadius: '50%',
                  marginRight: '4px',
                  marginLeft: '4px',
                  flexShrink: 0, // Prevent the circle from shrinking
                }}
              ></div>
              <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start', marginLeft:'2px'}}>
                <div style={{fontSize: `${fontSize*.8}px`, fontWeight:'400'}}>Inactive</div>
                <div style={{fontSize: `${fontSize*.95}px`, fontWeight:'500'}}> {(inactive / (totalUsers) * 100).toFixed(2)}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveParticipants;
